import { isFreemiumUser } from '@/utils/authorization/user-products'

/**
 * Initializes the Wootric feedback widget for eligible users.
 *
 * Dynamically loads the Wootric SDK and configures it with user information.
 * The function will not initialize Wootric for freemium users or Mobile Action users.
 *
 * @async
 * @function
 * @param {object} userInfo - User information object
 * @param {string} userInfo.username - User's email address
 * @param {string} userInfo.createdAt - Account creation date in ISO format
 * @param {string} userInfo.accountId - User's account ID
 * @param {string} userInfo.userId - User's ID
 * @param {object} [userInfo.productMap] - Optional map of product properties
 * @param {boolean} isMaUser - Whether the user is a Mobile Action user
 * @returns {Promise<void>}
 */
export async function initializeWootric(userInfo, isMaUser) {
  // Skip initialization for freemium or Mobile Action users
  if (isMaUser || await isFreemiumUser()) {
    return
  }

  try {
    await loadWootricScript()
    configureWootric(userInfo)
    if (window.wootric) {
      window.wootric('run')
    }
  }
  catch (error) {
    console.error('Failed to initialize Wootric:', error)
  }
}

/**
 * Loads the Wootric SDK script.
 *
 * @returns {Promise<void>} Resolves when script is loaded, rejects on error
 */
function loadWootricScript() {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = 'https://cdn.wootric.com/wootric-sdk.js'

    script.onload = () => resolve()
    script.onerror = error => reject(new Error(`Failed to load Wootric SDK: ${error}`))

    document.head.appendChild(script)
  })
}

/**
 * Configures Wootric with user information.
 *
 * @param {object} userInfo - User information object
 */
function configureWootric(userInfo) {
  window.wootricSettings = {
    email: userInfo.username,
    created_at: new Date(userInfo.createdAt).getTime() / 1000,
    account_token: 'NPS-396c2da6',
    product_name: 'mobileaction',
    properties: {
      product_name: 'mobileaction',
      account_id: userInfo.accountId,
      user_id: userInfo.userId,
    },
  }

  if (userInfo.productMap) {
    addProductProperties(userInfo.productMap)
  }
}

/**
 * Adds product-specific properties to Wootric settings.
 *
 * @param {object} productMap - Map of product properties
 */
function addProductProperties(productMap) {
  Object.entries(productMap).forEach(([key, value]) => {
    window.wootricSettings.properties[`p_${key}`] = value
  })
}
